import { DataTestId, TextKey } from '@mews-commander/enums';
import { LocalizedMinorHighlight, LocalizedSmallParagraph } from '@mews-commander/ui';
import {
    dataSecurity2FA1,
    dataSecurity2FA2,
    dataSecurity2FA3,
    dataSecurityTrustedDevices1,
    dataSecurityTrustedDevices2,
    dataSecurityTrustedDevices3,
    dataSecurityWidgetImage as dataSecurityImagePath,
    dataSecurityBeatPhishing as beatPhishingImagePath,
    dataSecurityPhishingRise as phishingRiseImagePath,
} from '@mews-commander/assets';
import { MewsImage } from '@mews/business-components';
import { Button } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { Widget } from '@mews-commander/dashboard/widgets';
import { openInBrowserTab } from '@mews-commander/utils';
import { DATA_SECURITY_URL, TRUSTED_DEVICES_URL, TWO_FA_URL } from '../constants';

interface Props {
    imagePath: string;
    headerTextKey: Mews.TextKey;
    paragraphTextKey: Mews.TextKey;
    url: string;
    buttonText: Mews.TextKey;
}

const imageHeight = '196px';

const DataSecurityBaseWidget = ({ buttonText, imagePath, headerTextKey, paragraphTextKey, url }: Props) => {
    const handleClick = () => openInBrowserTab(url);

    return (
        <Widget titleTextKey={TextKey.DashboardWidgetDataSecurityTitleText}>
            <MewsImage
                src={imagePath}
                height={imageHeight}
                objectFit="contain"
                objectPosition="left top"
                shouldShowLoader
            />
            <LocalizedMinorHighlight textKey={headerTextKey} />
            <LocalizedSmallParagraph textKey={paragraphTextKey} />
            <div>
                <Button onClick={handleClick} data-test-id={DataTestId.DashboardWidgetDataSecurityButton} type="submit">
                    <LocalizedText textKey={buttonText} />
                </Button>
            </div>
        </Widget>
    );
};

export const DataSecurityWidget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurityImagePath}
        buttonText={TextKey.DashboardWidgetDataSecurityButtonText}
        headerTextKey={TextKey.DashboardWidgetDataSecurityHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetDataSecurityText}
        url={DATA_SECURITY_URL}
    />
);

export const BeatPhishingWidget = () => (
    <DataSecurityBaseWidget
        imagePath={beatPhishingImagePath}
        buttonText={TextKey.DashboardWidgetDataSecurityButtonText}
        headerTextKey={TextKey.DashboardWidgetBeatPhishingHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetBeatPhishingText}
        url={DATA_SECURITY_URL}
    />
);

export const PhishingRiseWidget = () => (
    <DataSecurityBaseWidget
        imagePath={phishingRiseImagePath}
        buttonText={TextKey.DashboardWidgetDataSecurityButtonText}
        headerTextKey={TextKey.DashboardWidgetPhishingRiseHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetBeatPhishingText}
        url={DATA_SECURITY_URL}
    />
);

export const Security2FA1Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurity2FA1}
        buttonText={TextKey.DashboardWidgetDiscoverMoreButtonText}
        headerTextKey={TextKey.DashboardWidgetPasswordBackupHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetPasswordBackupText}
        url={TWO_FA_URL}
    />
);

export const Security2FA2Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurity2FA2}
        buttonText={TextKey.DashboardWidgetDiscoverMoreButtonText}
        headerTextKey={TextKey.DashboardWidget2FASecureHeaderText}
        paragraphTextKey={TextKey.DashboardWidget2FASecureText}
        url={TWO_FA_URL}
    />
);

export const Security2FA3Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurity2FA3}
        buttonText={TextKey.DashboardWidgetDiscoverMoreButtonText}
        headerTextKey={TextKey.DashboardWidgetStayAheadHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetStayAheadText}
        url={TWO_FA_URL}
    />
);

export const TrustedDevices1Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurityTrustedDevices1}
        buttonText={TextKey.DashboardWidgetDiscoverMoreButtonText}
        headerTextKey={TextKey.DashboardWidgetProtectAccountHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetProtectAccountText}
        url={TRUSTED_DEVICES_URL}
    />
);

export const TrustedDevices2Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurityTrustedDevices2}
        buttonText={TextKey.DashboardWidgetDiscoverMoreButtonText}
        headerTextKey={TextKey.DashboardWidgetTrustedDevicesHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetTrustedDevicesText}
        url={TRUSTED_DEVICES_URL}
    />
);

export const TrustedDevices3Widget = () => (
    <DataSecurityBaseWidget
        imagePath={dataSecurityTrustedDevices3}
        buttonText={TextKey.DashboardWidgetMoreOnSecurityButtonText}
        headerTextKey={TextKey.DashboardWidgetExtraSecurityHeaderText}
        paragraphTextKey={TextKey.DashboardWidgetExtraSecurityText}
        url={TRUSTED_DEVICES_URL}
    />
);
